import React, {useState, useEffect} from 'react';
import Select from 'react-select';
import './App.css';
import {useTranslation} from 'react-i18next';
import PropsInput from './PropsInput';
import SumInput from './SumInput';

function Advance() {
    const {t} = useTranslation();

    const [posts, setPosts] = useState([]);
    const [selectedPost, setSelectedPost] = useState(null);
    const [isPostValid, setIsPostValid] = useState(true);
    const handlePostChange = (option) => {
        setSelectedPost(option);
        setIsPostValid(!!option);
    };

    const [paymentTurnovers, setPaymentTurnovers] = useState([]);
    const [selectedPaymentTurnover, setSelectedPaymentTurnover] = useState(null);
    const [isPaymentTurnoverValid, setIsPaymentTurnoverValid] = useState(true);
    const handlePaymentTurnoverChange = (option) => {
        setSelectedPaymentTurnover(option);
        setIsPaymentTurnoverValid(!!option);
    };

    const validateDropdowns = () => {
        const postValid = selectedPost !== null;
        const turnoverValid = selectedPaymentTurnover !== null;

        setIsPostValid(postValid);
        setIsPaymentTurnoverValid(turnoverValid);

        return postValid && turnoverValid;
    };

    const [documentNumber, setDocumentNumber] = useState('');
    const [isDocumentNumberValid, setIsDocumentNumberValid] = useState(true);
    const [documentNumberErrorText, setDocumentNumberErrorText] = useState('');
    const handleDocumentNumberStrChange = (value) => {
        setDocumentNumber(value);
    };
    const validateDocumentNumber = (value) => {
        const isValid = /^(\d{14}|\d{14}\/\d{8})$/.test(value);
        setIsDocumentNumberValid(isValid);
        setDocumentNumberErrorText(t('Enter only TIN or TIN/OKPO'));

        return isValid;
    };

    const [purposeStr, setPurposeStr] = useState('');
    const handlePurposeStrChange = (event) => {
        setPurposeStr(event.target.value);
    };

    const [amount, setAmount] = useState('');
    const [isAmountValid, setIsAmountValid] = useState(true);
    const validateAmount = () => {
        const numericValue = parseFloat(amount.toString().replace(',', '.'));

        const isValid = !isNaN(numericValue) && numericValue > 0;

        setIsAmountValid(isValid);

        return isValid;
    };

    const [commentStr, setCommentStr] = useState('');
    const handleCommentStrChange = (event) => {
        setCommentStr(event.target.value);
    };

    const [qrCodeUrl, setQrCodeUrl] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const handleGenerateQr = async () => {

        const isDocValid = validateDocumentNumber(documentNumber);
        const isDropdownsValid = validateDropdowns();
        const isAmountValid = validateAmount();

        if (!isDocValid || !isDropdownsValid || !isAmountValid)
            return;

        setIsLoading(true);

        const params = new URLSearchParams({
            postCode: selectedPost.value,
            paymentTurnoverCode: selectedPaymentTurnover.value,
            documentNumber: documentNumber,
            purpose: purposeStr,
            amount: amount.toString(),
            comment: commentStr,
        });

        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const secretKey = process.env.REACT_APP_X_SECRET_KEY;
        const url = `${baseUrl}/qr/advance?${params.toString()}`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Accept: 'image/png',
                    'X-Secret-Key': secretKey,
                },
            });

            if (!response.ok) {
                setIsDocumentNumberValid(false);
                const data = await response.json();
                setDocumentNumberErrorText(data.messageDetail);
            }

            const blob = await response.blob();

            if (qrCodeUrl) {
                URL.revokeObjectURL(qrCodeUrl);
            }

            const imageUrl = URL.createObjectURL(blob);
            setQrCodeUrl(imageUrl);

        } catch (error) {
            console.error("Error fetching QR code:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const baseUrl = process.env.REACT_APP_API_BASE_URL;
                const secretKey = process.env.REACT_APP_X_SECRET_KEY;
                const url = `${baseUrl}/posts`;
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'X-Secret-Key': secretKey,
                    },
                });
                const data = await response.json();

                const options = data.map(post => ({
                    value: post.code,
                    label: post.name
                }));

                setPosts(options);
            } catch (error) {
                console.error('Error fetching posts:', error);
            }
        };

        const fetchPaymentTurnovers = async () => {
            try {
                const baseUrl = process.env.REACT_APP_API_BASE_URL;
                const secretKey = process.env.REACT_APP_X_SECRET_KEY;
                const url = `${baseUrl}/payment-turnovers`;
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'X-Secret-Key': secretKey,
                    },
                });
                const data = await response.json();

                const options = data.map(paymentTurnover => ({
                    value: paymentTurnover.code,
                    label: `${paymentTurnover.name}(${paymentTurnover.code})`
                }));

                setPaymentTurnovers(options);
            } catch (error) {
                console.error('Error fetching paymentTurnovers:', error);
            }
        };

        fetchPosts();
        fetchPaymentTurnovers();
    }, []);

    const wrapWordStyle = {
        control: (provided) => ({
            ...provided,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            minHeight: '38px',
            alignItems: 'flex-start',
            paddingTop: '4px',
            paddingBottom: '4px',
            width: '100%',
        }),
        singleValue: (provided) => ({
            ...provided,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: '1.2',
        }),
        option: (provided) => ({
            ...provided,
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            padding: 10,
            maxWidth: '500px',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            maxWidth: '500px',
            width: '100%',
        }),
    };

    return (
        <div className="input-container">
            <div className="form-group">
                <div className="form-label">{t('Customs')}</div>
                <div style={{flex: 1}}>
                    <Select
                        value={selectedPost}
                        onChange={handlePostChange}
                        options={posts}
                        placeholder={t('Select a customs')}
                        isClearable
                        isSearchable
                        styles={{
                            ...wrapWordStyle,
                            control: (base, state) => ({
                                ...wrapWordStyle.control(base, state),
                                borderColor: isPostValid ? base.borderColor : 'red',
                                boxShadow: isPostValid ? base.boxShadow : '0 0 0 1px red',
                            })
                        }}
                    />
                </div>
            </div>

            <div className="form-group">
                <div className="input-label"></div>
                {!isPostValid && (
                    <div className="error-text">{t('Please select a customs')}</div>
                )}
            </div>

            <div className="form-group">
                <div className="form-label">{t('Payment Code')}</div>
                <div style={{flex: 1}}>
                    <Select
                        value={selectedPaymentTurnover}
                        onChange={handlePaymentTurnoverChange}
                        options={paymentTurnovers}
                        placeholder={t('Select a payment turnover')}
                        isClearable
                        isSearchable
                        styles={{
                            ...wrapWordStyle,
                            control: (base, state) => ({
                                ...wrapWordStyle.control(base, state),
                                borderColor: isPaymentTurnoverValid ? base.borderColor : 'red',
                                boxShadow: isPaymentTurnoverValid ? base.boxShadow : '0 0 0 1px red',
                            })
                        }}
                    />
                </div>
            </div>

            <div className="form-group">
                <div className="input-label"></div>
                {!isPaymentTurnoverValid && (
                    <div className="error-text">{t('Please select a payment turnover')}</div>
                )}
            </div>

            <div className="form-group">
                <div className="form-label">{t('Props')}</div>
                <div>
                    <PropsInput value={documentNumber} onChange={handleDocumentNumberStrChange}
                                isValid={isDocumentNumberValid}/>
                    <div className="helper-text">{t('Enter TIN and OKPO')}</div>
                </div>
            </div>
            {!isDocumentNumberValid && (
                <div className="form-group">
                    <div className="input-label"></div>
                    <div className="error-text">{documentNumberErrorText}</div>
                </div>
            )}

            <div className="form-group">
                <div className="form-label">{t('Purpose')}</div>
                <input type="text" value={purposeStr} onChange={handlePurposeStrChange}/>
            </div>

            <div className="form-group">
                <div className="form-label">{t('Amount')}</div>
                <div className="form-control">
                    <SumInput value={amount} onChange={setAmount} placeholder={t('Enter the amount')}
                              isValid={isAmountValid}/>
                </div>
            </div>

            <div className="form-group">
                <div className="input-label"></div>
                {!isAmountValid && (
                    <div className="error-text">{t('Please enter a amount')}</div>
                )}
            </div>

            <div className="form-group">
                <div className="form-label">{t('Commentary')}</div>
                <input type="text" value={commentStr} onChange={handleCommentStrChange}
                       placeholder={t('Enter the comment')}/>
            </div>

            <div className="form-group">
                <div className="form-label"></div>
                <button
                    className="form-btn"
                    onClick={handleGenerateQr}
                    disabled={isLoading}
                >{isLoading ? (
                    <>
                        <span className="spinner"/> {t('Loading...')}
                    </>
                ) : (
                    t('Generate QR')
                )}
                </button>
            </div>

            {qrCodeUrl && (
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: 20,
                    width: "100%",
                }}>
                    <h3>{t('Pay QR')}</h3>
                    <img src={qrCodeUrl} alt="QR Code" style={{
                        maxWidth: "100%",
                        height: "auto",
                        maxHeight: 400,
                        objectFit: "contain",
                        marginTop: 10,
                    }}/>
                </div>
            )}
        </div>
    );
}

export default Advance;
